import camelcaseKeys from 'camelcase-keys';
import cache from 'lscache';
import { t } from 'i18next';

import { buildHeaders, buildUrl, buildRequest, fetchRequest } from '../../utils/apis/RestApi';
import {
  type AvailableArticle,
  type MasterDataArticle,
  type MasterDataArticleRaw,
  type CatalogProduct,
  type MasterDataModelArticleRaw,
} from './masterdata.type';
import { chunkArray, getProductInformation, getProductsFromCatalog, iLinkRanges } from '../ilink';
import { minutesToMidnight } from '../../utils/utils';
import { getModelsInformation } from '../spid';
import { handleError } from '../../business/helpers';
import { getArticleIdFromCode } from '../../business/referenceCodeHelper';
import { type Brand } from '../dictionaries.type';
import { type LifeStage } from '../../business/articleHelper';
import { cacheErrorLog, createLog } from '../logger';
import HttpError from '../../utils/apis/HttpError';
import { SERVICE_NATURE_ID } from './constants';

const masterdataStorageKey = 'masterdata';

const logger = createLog('MasterData API');

export const checkAvailabilityForArticles = async (articleIds: number[], lifestages: LifeStage[], storeId: number): Promise<AvailableArticle[]> => {
  if (articleIds.some(articleId => Number.isNaN(articleId))) {
    throw new Error(t('error.message.invalid.searched.text'));
  }

  const headers = buildHeaders();
  const url = buildUrl(
    'ICARE_BACK',
    `masterdata/check_availability_for_articles/${storeId}/${articleIds}?lifeStages=${lifestages}&range=${iLinkRanges}`
  );
  const request = buildRequest(url, 'GET', headers);
  try {
    const response = await fetchRequest(request);
    return await response.json();
  } catch {
    return handleError('MasterData Request - GET Check available article');
  }
};

export const getArticlesDetails = (articleIds: (number | string)[]) => {
  const url = buildUrl('ICARE_BACK', `masterdata/articles_information/${articleIds}`);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => response.json())
    .then(data => camelcaseKeys<MasterDataArticleRaw[]>(data, { deep: true }))
    .catch(error => {
      if (error.response?.status === 404) {
        return [] as MasterDataArticleRaw[];
      }
      return handleError('MasterData Request - Articles details search');
    });
};

export const isServiceNature = (modelCode: number): Promise<boolean> => {
  const url = buildUrl('ICARE_BACK', `masterdata/product_nature/${modelCode}`);
  const headers = buildHeaders();
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => response.json())
    .then(result => result.natureId === SERVICE_NATURE_ID)
    .catch(() => false);
};

export const getArticleIdFromEAN = (eanCode: number): Promise<number> => {
  const key = `${masterdataStorageKey}-get-articleId-by-ean-${eanCode}`;
  const storedArticleId = cache.get(key);
  if (storedArticleId) {
    return Promise.resolve(storedArticleId);
  }
  const headers = buildHeaders();
  const url = buildUrl('ICARE_BACK', `masterdata/v2/arbo/items/search?ean=${eanCode}&item_type=ARTICLE`);
  const request = buildRequest(url, 'GET', headers);

  return fetchRequest(request)
    .then(response => response.json())
    .then(data => {
      const result = Number.parseInt(data.supplier_code, 10);
      try {
        cache.set(key, result, minutesToMidnight());
      } catch (error) {
        cacheErrorLog(error);
      }
      return result;
    })
    .catch(() => handleError('MasterData Request - GET ArticleId from EAN Code'));
};

export const getModelBrand = (modelCode: number): Promise<Brand> => {
  const key = `${masterdataStorageKey}-model-brand-${modelCode}`;
  const storedModelBrand = cache.get(key);

  if (storedModelBrand) {
    return Promise.resolve(JSON.parse(storedModelBrand));
  }

  const headers = buildHeaders();
  const url = buildUrl('ICARE_BACK', `masterdata/v2/brands/models/${modelCode}`);
  const request = buildRequest(url, 'GET', headers);

  return fetchRequest(request)
    .then(response => response.json())
    .then(result => {
      const brand: Brand = {
        id: result.id,
        name: result.brand_name,
      };
      try {
        cache.set(key, JSON.stringify(brand), minutesToMidnight());
      } catch (error) {
        cacheErrorLog(error);
      }
      return brand;
    })
    .catch(() => handleError('MasterData Request - GET Model Brand'));
};

const getExistingOrUnknownIds = (ids: number[]) =>
  ids
    .map(id => cache.get(`${masterdataStorageKey}-${id}`) as MasterDataArticle[])
    .reduce(
      (acc: [MasterDataArticle[][], number[]], cachedModel, index) => {
        if (cachedModel) {
          acc[0].push(cachedModel);
        } else {
          acc[1].push(ids[index]);
        }
        return acc;
      },
      [[], []]
    );

const buildMasterDataArticle =
  (selected = false) =>
  (article: MasterDataArticleRaw): MasterDataArticle[] =>
    article.gridInfos.map(gridInfo => ({
      articleId: Number.parseInt(article.itemId, 10),
      modelCode: Number.parseInt(article.modelId, 10),
      gridId: gridInfo.numGrid,
      gridValueId: gridInfo.valGridId,
      labelGrid: gridInfo.valGridLabel,
      selected,
      eanNum: article.eanCode,
      numOrder: article.numOrder,
      modelLib: article.modelLib,
      unitQuantity: article.ueQuantity,
      nature: article.nature,
    }));

async function fetchArticlesFromModelCode(modelCode: number): Promise<MasterDataModelArticleRaw[]> {
  try {
    const url = buildUrl('ICARE_BACK', `masterdata/v2/arbo/models/${modelCode}/articles`);
    const headers = buildHeaders();
    headers.set('accept-language', '');
    const request = buildRequest(url, 'GET', headers);

    const response = await fetchRequest(request);

    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      void logger.log(error);
    }
    return [];
  }
}

export const getArticles = async (modelCodes: number[]): Promise<MasterDataArticle[]> => {
  try {
    const [existingModels, newModels] = getExistingOrUnknownIds(modelCodes);

    const articles: MasterDataModelArticleRaw[] = [];

    const chunkNewModels = chunkArray(newModels, 10);

    for (const chunkNewModel of chunkNewModels) {
      const chunkArticlesFromModelCode = await Promise.all(chunkNewModel.map(model => fetchArticlesFromModelCode(model)));
      const flattenArticles = chunkArticlesFromModelCode.flat();

      articles.push(...flattenArticles);
    }

    const chunkArticles = chunkArray(articles, 25);

    const articlesDetails = await Promise.all(
      chunkArticles.map(articleIds => getArticlesDetails(articleIds.map(article => article.article_id)))
    ).then(result => result.flat());

    const masterDataArticles = articlesDetails.flatMap(buildMasterDataArticle(articles.length === 1));

    newModels.forEach(modelCode => {
      const key = `${masterdataStorageKey}-${modelCode}`;
      try {
        cache.set(
          key,
          masterDataArticles.filter(article => article.modelCode === modelCode),
          minutesToMidnight()
        );
      } catch (error) {
        cacheErrorLog(error);
      }
    });

    return existingModels.flat().concat(masterDataArticles);
  } catch (error) {
    if (error instanceof HttpError) {
      if (error.response?.status === 404) {
        return [];
      }
    }

    return handleError('MasterData Request - Articles search');
  }
};

export const getProductFromCatalog = (searchedCode: string): Promise<CatalogProduct | null> => {
  if (Number.isNaN(Number(searchedCode))) {
    return Promise.resolve(null);
  }

  const key = `${masterdataStorageKey}-catalog-product-${searchedCode}`;
  const storedCatalogProduct = cache.get(key);
  if (storedCatalogProduct) {
    return Promise.resolve(storedCatalogProduct);
  }

  return getArticleIdFromCode(searchedCode).then(async code => {
    let articles = await getArticles([code]);
    if (articles.length === 0) {
      const articlesRaw = await getArticlesDetails([code]);
      articles = articlesRaw.flatMap(buildMasterDataArticle(articlesRaw.length === 1));
    }
    const productCode = articles[0]?.modelCode;

    if (productCode) {
      const productInformation = await getProductInformation(productCode);
      const catalogProducts = productInformation ? await getProductsFromCatalog(productInformation.category.id) : [];
      const isProductFromCatalog = catalogProducts.map(catalogModel => catalogModel.model_code).includes(productCode);

      const [[modelInformation], brand] = await Promise.all([getModelsInformation([productCode]), getModelBrand(productCode)]);

      const result: CatalogProduct = {
        product: {
          id: String(modelInformation.modelCode),
          label: modelInformation.label,
          imageUrl: modelInformation.imageUrl,
          isCustom: !isProductFromCatalog,
          articles,
          brand,
          process: {
            id: productInformation?.process.id ?? null,
            label: productInformation?.process.name ?? '',
          },
          category: {
            id: productInformation?.category.id ?? null,
            label: productInformation?.category.name ?? '',
          },
        },
        productInformation,
      };

      try {
        cache.set(key, result, minutesToMidnight());
      } catch (error) {
        cacheErrorLog(error);
      }
      return result;
    }
    return null;
  });
};
